import React, { useState, useContext, useRef, useEffect } from 'react';
import { BoxContext } from './BoxContext';
import moment from 'moment';

const SendEmail = ({ recipientEmail, onClose }) => {
  const { selectedNickname, shipmentInfo } = useContext(BoxContext);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 500, height: 'auto' });
  const modalRef = useRef(null);
  
  // Form fields
  const [staffComment, setStaffComment] = useState('');
  const [timeOfComment, setTimeOfComment] = useState(moment().format('ddd, MMM Do [at] h:mm A'));
  const [employeeName, setEmployeeName] = useState(selectedNickname || '');
  const [outboundName, setOutboundName] = useState(shipmentInfo?.name || '');
  const [outboundLink, setOutboundLink] = useState(`https://app.yyzprep.ca/shipments/outbound/${shipmentInfo?.id || ''}`);
  const [attachments, setAttachments] = useState([]);

  const handleFileChange = (e) => {
    setAttachments([...e.target.files]);
  };

  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };


  // Generate full message from fields
  const generateMessage = () => {
    return `Staff Comment:
${staffComment}

Time of Comment: ${timeOfComment}
Employee Name: ${employeeName}
Outbound Name: ${outboundName}
Outbound Link: ${outboundLink}

Please provide any information requested, or process any adjustments or changes as needed ASAP and let us know.

Send us an email, text, or call to notify us of any changes that may be necessary. Due to the volume of shipments being processed, and depending on your shipment type, it may be adjusted to ship out to improve lead times.

Kind Regards,
YYZ Prep Team`;
  };

  // Center the modal initially
  useEffect(() => {
    if (modalRef.current) {
      const { width, height } = modalRef.current.getBoundingClientRect();
      setPosition({
        x: (window.innerWidth - width) / 2,
        y: Math.max(20, (window.innerHeight - height) / 3)
      });
    }
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('.modal-header')) {
      setIsDragging(true);
      setStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setStartPos({
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y
      });
    } else if (isResizing) {
      const newWidth = dimensions.width + (e.clientX - startPos.x);
      setDimensions({
        ...dimensions,
        width: Math.max(300, newWidth) // Set minimum width to 300px
      });
      setStartPos({
        x: e.clientX,
        y: e.clientY
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  // Add global mouse event listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, startPos, dimensions.width]);
  
  const sendEmail = async () => {
    const formData = new FormData();
    formData.append('clientEmail', recipientEmail);
    formData.append('subject', `[ACTION REQUIRED] YYZ Prep | Outbound Shipment Issue - ID: ${shipmentInfo?.id}`);
    formData.append('text', generateMessage());
    attachments.forEach(file => {
      formData.append('attachments', file);
    });

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/send-email`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      alert('Email sent successfully');
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email');
    }
  };

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      <div 
        ref={modalRef}
        onMouseDown={handleMouseDown}
        className="bg-white rounded-xl shadow-2xl relative max-w-xl pointer-events-auto flex flex-col"
        style={{ 
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${dimensions.width}px`,
          height: dimensions.height,
          maxHeight: '90vh'
        }}
      >
                  <div className="modal-header px-6 py-4 flex justify-between items-center cursor-move rounded-t-lg bg-blue-600 text-white">
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <h2 className="text-lg font-semibold">Email to {recipientEmail}</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200 focus:outline-none transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Staff Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              value={staffComment}
              onChange={(e) => setStaffComment(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              rows="3"
              placeholder="Enter your comment here..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachments
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                    <span>Upload files</span>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple onChange={handleFileChange} />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
              </div>
            </div>
          </div>
          {attachments.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700">Selected files:</h3>
              <ul className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-200">
                {attachments.map((file, index) => (
                  <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                    <div className="w-0 flex-1 flex items-center">
                      <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a3 3 0 003 3h4a3 3 0 003-3V7a3 3 0 00-3-3H8zm0 1.5a1.5 1.5 0 011.5 1.5v4a1.5 1.5 0 01-1.5 1.5h-4A1.5 1.5 0 014 11.5V7A1.5 1.5 0 015.5 5H8z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-2 flex-1 w-0 truncate">{file.name}</span>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <button onClick={() => removeAttachment(index)} className="font-medium text-blue-600 hover:text-blue-500">
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time of Comment
              </label>
              <input
                type="text"
                value={timeOfComment}
                onChange={(e) => setTimeOfComment(e.target.value)}
                className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee Name
              </label>
              <input
                type="text"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Outbound Name
            </label>
            <input
              type="text"
              value={outboundName}
              onChange={(e) => setOutboundName(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Outbound Link
            </label>
            <input
              type="text"
              value={outboundLink}
              onChange={(e) => setOutboundLink(e.target.value)}
              className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="pt-2">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700">
              <p className="font-medium mb-1">Preview:</p>
              <p className="whitespace-pre-line">{generateMessage().split('\n\n')[0]}</p>
              <p className="mt-1 text-blue-500">+ additional standard message content</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 flex justify-end space-x-3 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={sendEmail}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Send Email
          </button>
        </div>
        
        {/* Resize handle */}
        <div 
          className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize flex items-center justify-center"
          onMouseDown={handleResizeMouseDown}
        >
          <svg 
            width="10" 
            height="10" 
            viewBox="0 0 10 10" 
            className="text-gray-400"
          >
            <path d="M0 0v10h10V0H0zm1 9V1h8v8H1z" fill="none"/>
            <path d="M8 2H6v2h2V2zM5 5H3v2h2V5zM8 5H6v2h2V5zM5 8H3v1h2V8zM8 8H6v1h2V8z" fill="currentColor"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default SendEmail; 